import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { brand, radius, shadow } from '../brand';
import { UseCase } from './types';

interface UseCaseSectionProps {
    useCase: UseCase;
    issued: string[];
}

// Renders any registered use case. Every string comes from
// `useCases.<id>.*` in the locale files, so a new use case is a data change.
export const UseCaseSection: FC<UseCaseSectionProps> = ({useCase, issued}) => {
    const {t} = useTranslation();
    const base = `useCases.${useCase.id}`;

    const missing = useCase.requiredCredentials.filter(credentialType => !issued.includes(credentialType));
    const ready = missing.length === 0;
    const missingNames = missing.map(credentialType => t(`credentials.${credentialType}.title`)).join(', ');

    return (
        <Box
            data-testid={`use-case-${useCase.id}`}
            sx={{
                background: brand.surface,
                border: `1px solid ${brand.border}`,
                borderRadius: radius.panel,
                boxShadow: shadow.card,
                padding: {xs: '28px 22px', md: '44px'},
            }}>
            <Stack spacing={4}>
                <Stack spacing={1.5}>
                    <Typography sx={{
                        color: brand.blue,
                        fontWeight: 700,
                        fontSize: '.78rem',
                        letterSpacing: '.12em',
                        textTransform: 'uppercase',
                    }}>
                        {t(`${base}.tagline`)}
                    </Typography>
                    {/* h3, not h4: the section heading above is an h2, and skipping a
                        level breaks heading navigation. variant keeps the visual size. */}
                    <Typography variant="h4" component="h3" sx={{
                        color: brand.navy,
                        fontWeight: 700,
                        letterSpacing: '-.02em',
                        fontSize: {xs: '1.6rem', md: '2rem'},
                    }}>
                        {t(`${base}.name`)}
                    </Typography>

                    {/* The address sits with the name, not with the button: the demo is
                        often run across two devices, so it has to be readable before
                        anyone reaches the call to action. */}
                    <Stack
                        component="a"
                        direction="row"
                        spacing={0.75}
                        href={useCase.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`use-case-url-${useCase.id}`}
                        sx={{
                            alignSelf: 'flex-start',
                            alignItems: 'center',
                            background: brand.backgroundAlt,
                            border: `1px solid ${brand.border}`,
                            borderRadius: radius.pill,
                            padding: '6px 14px',
                            color: brand.blue,
                            fontSize: '.9rem',
                            fontWeight: 600,
                            letterSpacing: '.01em',
                            textDecoration: 'none',
                            wordBreak: 'break-all',
                            transition: 'background .15s ease, border-color .15s ease',
                            '&:hover': {
                                background: brand.accentLight,
                                borderColor: brand.accentSoft,
                            },
                        }}>
                        <span>{useCase.url.replace(/^https:\/\//, '').replace(/\/$/, '')}</span>
                        <OpenInNewRoundedIcon sx={{fontSize: 15}}/>
                    </Stack>

                    <Typography sx={{color: brand.text, maxWidth: '64ch', fontSize: '1.02rem', lineHeight: 1.6}}>
                        {t(`${base}.intro`)}
                    </Typography>
                </Stack>

                <Stack spacing={2}>
                    <Typography sx={{color: brand.navy, fontWeight: 700, fontSize: '1.05rem'}}>
                        {t(`${base}.walkthroughTitle`)}
                    </Typography>
                    <Stack spacing={0}>
                        {useCase.walkthrough.map((step, index) => (
                            <Stack
                                key={step.titleKey}
                                direction="row"
                                spacing={2}
                                sx={{
                                    paddingBottom: index === useCase.walkthrough.length - 1 ? 0 : '20px',
                                    borderLeft: index === useCase.walkthrough.length - 1
                                        ? '2px solid transparent'
                                        : `2px solid ${brand.accentSoft}`,
                                    marginLeft: '15px',
                                    paddingLeft: '26px',
                                    position: 'relative',
                                }}>
                                <Box sx={{
                                    position: 'absolute',
                                    left: '-15px',
                                    top: 0,
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    background: brand.navy,
                                    color: '#FFFFFF',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '.82rem',
                                    fontWeight: 700,
                                    fontVariantNumeric: 'tabular-nums',
                                }}>
                                    {index + 1}
                                </Box>
                                <Stack spacing={0.5} sx={{paddingTop: '3px'}}>
                                    <Typography sx={{color: brand.navy, fontWeight: 700, fontSize: '1rem'}}>
                                        {t(`${base}.${step.titleKey}`)}
                                    </Typography>
                                    <Typography sx={{color: brand.text, fontSize: '.94rem', lineHeight: 1.55, maxWidth: '60ch'}}>
                                        {t(`${base}.${step.bodyKey}`)}
                                    </Typography>
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>

                <Stack spacing={1.5}>
                    <Button
                        href={useCase.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="contained"
                        endIcon={<ArrowForwardIcon sx={{fontSize: 18, color: brand.green}}/>}
                        data-testid={`use-case-cta-${useCase.id}`}
                        sx={{
                            alignSelf: 'flex-start',
                            borderRadius: radius.pill,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '1rem',
                            padding: '11px 26px',
                            boxShadow: 'none',
                            background: ready ? brand.navy : brand.border,
                            color: ready ? '#FFFFFF' : brand.text,
                            '&:hover': {
                                background: ready ? brand.navy800 : brand.border,
                                boxShadow: 'none',
                            },
                        }}>
                        {ready ? t(`${base}.cta`) : t(`${base}.ctaBlocked`)}
                    </Button>

                    {/* The link stays clickable on purpose: a dead button with no
                        explanation is worse than a warning someone can overrule. */}
                    {!ready && (
                        <Stack
                            direction="row"
                            spacing={1.25}
                            data-testid={`use-case-warning-${useCase.id}`}
                            sx={{
                                background: '#FFF8E8',
                                border: '1px solid #F0DCA8',
                                borderRadius: radius.card,
                                padding: '14px 18px',
                                maxWidth: '64ch',
                            }}>
                            <InfoOutlinedIcon sx={{color: '#8A6A12', fontSize: 20}}/>
                            <Typography sx={{color: '#6B520C', fontSize: '.93rem', lineHeight: 1.55}}>
                                {t(`${base}.blockedWarning`, {missing: missingNames})}
                            </Typography>
                        </Stack>
                    )}
                </Stack>

                <Stack
                    spacing={1.25}
                    sx={{
                        background: brand.backgroundAlt,
                        borderRadius: radius.card,
                        padding: '20px 22px',
                    }}>
                    <Typography sx={{color: brand.navy, fontWeight: 700, fontSize: '.98rem'}}>
                        {t(`${base}.troubleshooting.title`)}
                    </Typography>
                    <Typography component="ul" sx={{
                        color: brand.text,
                        fontSize: '.93rem',
                        lineHeight: 1.6,
                        margin: 0,
                        paddingLeft: '20px',
                        maxWidth: '68ch',
                    }}>
                        <li>{t(`${base}.troubleshooting.noCredentials`)}</li>
                        <li>{t(`${base}.troubleshooting.staleSession`)}</li>
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    );
};
